// АВ.1: пользовательский аватар (заменяет HeroAvatar для отображения
// «лица игрока» в Home, Profile и т.д.).
//
// Эффект «глитч / помехи со связью» — RGB-сдвиг (одна цветная копия по
// направлению, другая по противоположному) + scan-lines + редкие
// «дёрги» по горизонтали раз в ~4 сек. Цвет сдвига берётся из каталога
// (catalog.ts → AVATAR_CATALOG[id].glitchColor). null = без эффекта.
//
// Если avatarId не задан / не в каталоге — рисуем SVG-плейсхолдер
// (силуэт игрока в цвете темы).

import { useState } from 'react';
import { getAvatarMeta } from '../avatars/catalog';

interface Props {
  avatarId: string | null | undefined;
  size?: number;             // px, default 56
  shape?: 'square' | 'circle';
  className?: string;
  // Можно подавить эффект (например, для маленьких иконок).
  disableEffect?: boolean;
}

const PLACEHOLDER_COLOR = '#9CFF3D';

export function UserAvatar({
  avatarId, size = 56, shape = 'square', className, disableEffect = false
}: Props) {
  const meta = getAvatarMeta(avatarId);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const radius = shape === 'circle' ? '50%' : '4px';
  const borderColor = meta?.glitchColor || PLACEHOLDER_COLOR;

  const baseStyle: React.CSSProperties = {
    width: size, height: size, flexShrink: 0,
    borderRadius: radius,
    border: `1.5px solid ${borderColor}`,
    background: 'var(--bg-3, #171723)',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  };

  // Плейсхолдер если нет id или картинка не загрузилась.
  if (!meta || failed) {
    return (
      <div className={className} style={baseStyle}>
        <svg
          width={Math.round(size * 0.5)}
          height={Math.round(size * 0.5)}
          viewBox="0 0 24 24"
          fill="none"
          stroke={borderColor}
          strokeWidth="1.5"
        >
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21a8 8 0 0 1 16 0" />
        </svg>
      </div>
    );
  }

  const enableGlitch = !disableEffect && !!meta.glitchColor;

  return (
    <div className={className} style={baseStyle} data-ua-glitch={enableGlitch ? meta.glitchColor : undefined}>
      <img
        src={meta.src}
        alt={meta.label}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        onLoad={() => setLoaded(true)}
        className="ua-base"
        style={{
          width: '100%', height: '100%',
          objectFit: 'cover', display: 'block',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.25s ease'
        }}
      />
      {enableGlitch && loaded && (
        <>
          {/* Цветной слой 1: смещён +1px, поверх с blend-mode screen. */}
          <img
            src={meta.src}
            aria-hidden
            className="ua-glitch ua-glitch-a"
            style={{
              ['--glitchColor' as any]: meta.glitchColor
            }}
          />
          {/* Цветной слой 2: смещён -1px, поверх. */}
          <img
            src={meta.src}
            aria-hidden
            className="ua-glitch ua-glitch-b"
            style={{
              ['--glitchColor' as any]: meta.glitchColor
            }}
          />
          {/* Scan-lines поверх — статичные горизонтальные полоски */}
          <div className="ua-scanlines" />
          {/* Редкие «дёрги» — отдельный слой для X-сдвига всей картинки */}
          <div className="ua-jitter" />
        </>
      )}
    </div>
  );
}

// Инжектим стили один раз — keyframes и фильтры.
if (typeof document !== 'undefined' && !document.getElementById('user-avatar-keyframes')) {
  const style = document.createElement('style');
  style.id = 'user-avatar-keyframes';
  style.textContent = `
    /* Цветной слой: проецируем картинку через color-фильтр (приближение
       к RGB-сдвигу): hue-rotate смещает все цвета, mix-blend-mode: screen
       подчёркивает выделенный canal. Сама картинка сдвигается влево/вправо. */
    .ua-glitch {
      position: absolute; top: 0; left: 0;
      width: 100%; height: 100%;
      pointer-events: none;
      object-fit: cover;
      mix-blend-mode: screen;
      opacity: 0.55;
    }
    .ua-glitch-a {
      transform: translate(1.2px, 0);
      filter: drop-shadow(2px 0 0 var(--glitchColor)) saturate(1.4);
      animation: ua-glitch-a 4.2s steps(1) infinite;
    }
    .ua-glitch-b {
      transform: translate(-1.2px, 0);
      filter: drop-shadow(-2px 0 0 var(--glitchColor)) saturate(1.4);
      animation: ua-glitch-b 4.2s steps(1) infinite;
    }
    /* Редкие «разрывы» — короткие резкие сдвиги по X и обрезание полос. */
    @keyframes ua-glitch-a {
      0%, 90%, 100% { transform: translate(1.2px, 0); clip-path: inset(0); opacity: 0.55; }
      92%           { transform: translate(-3px, 0); clip-path: inset(8% 0 70% 0); opacity: 0.85; }
      94%           { transform: translate(4px, 0);  clip-path: inset(45% 0 30% 0); opacity: 0.8; }
      96%           { transform: translate(-2px, 0); clip-path: inset(75% 0 5% 0); opacity: 0.75; }
    }
    @keyframes ua-glitch-b {
      0%, 90%, 100% { transform: translate(-1.2px, 0); clip-path: inset(0); opacity: 0.55; }
      92%           { transform: translate(3px, 0);  clip-path: inset(20% 0 55% 0); opacity: 0.85; }
      94%           { transform: translate(-4px, 0); clip-path: inset(55% 0 18% 0); opacity: 0.8; }
      96%           { transform: translate(2px, 0);  clip-path: inset(5% 0 80% 0); opacity: 0.75; }
    }
    /* Сканлайны — еле заметные горизонтальные полоски, как у CRT. */
    .ua-scanlines {
      position: absolute; inset: 0; pointer-events: none;
      background-image: repeating-linear-gradient(
        to bottom,
        rgba(0,0,0,0.0) 0px,
        rgba(0,0,0,0.0) 2px,
        rgba(0,0,0,0.22) 2px,
        rgba(0,0,0,0.22) 3px
      );
      mix-blend-mode: multiply;
      opacity: 0.55;
    }
    /* Редкое смещение картинки по X — лёгкая «рябь». */
    .ua-jitter {
      position: absolute; inset: 0; pointer-events: none;
      animation: ua-jitter 6.3s steps(1) infinite;
    }
    @keyframes ua-jitter {
      0%, 95%, 100% { transform: translate(0, 0); }
      96%  { transform: translate(1px, 0); }
      97%  { transform: translate(-2px, 0); }
      98%  { transform: translate(1px, 0); }
    }
  `;
  document.head.appendChild(style);
}
